import { X, Package, Edit, AlertTriangle } from 'lucide-react';

export default function InventoryDetailsModal({ item, isOpen, onClose, onEdit }) {
  if (!isOpen || !item) return null;

  const isLowStock = item.reorderPoint !== null && 
    parseFloat(item.quantityOnHand) <= parseFloat(item.reorderPoint);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-gray-50 px-4 py-5 sm:px-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Package className="h-6 w-6 text-blue-600" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {item.itemNumber}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {item.description}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  item.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {item.isActive ? 'Active' : 'Inactive'}
                </span>
                {isLowStock && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Low Stock
                  </span>
                )}
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-4 py-5 sm:p-6">
            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Basic Information</h4>
                <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Item Number</dt>
                    <dd className="mt-1 text-sm text-gray-900">{item.itemNumber}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        item.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {item.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">Description</dt>
                    <dd className="mt-1 text-sm text-gray-900">{item.description}</dd>
                  </div>
                  {item.purchaseDescription && item.purchaseDescription !== item.description && (
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">Purchase Description</dt>
                      <dd className="mt-1 text-sm text-gray-900">{item.purchaseDescription}</dd>
                    </div>
                  )}
                  {item.manufacturerPartNumber && (
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">Manufacturer Part Number (MPN)</dt>
                      <dd className="mt-1 text-sm text-gray-900">{item.manufacturerPartNumber}</dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Inventory & Pricing */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Inventory & Pricing</h4>
                <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Quantity On Hand</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      <div className="flex items-center">
                        <span>{parseFloat(item.quantityOnHand).toFixed(2)}</span>
                        {isLowStock && (
                          <AlertTriangle className="h-4 w-4 text-yellow-600 ml-2" title="Below reorder point" />
                        )}
                      </div>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Reorder Point</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {item.reorderPoint ? parseFloat(item.reorderPoint).toFixed(2) : 'Not set'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Cost</dt>
                    <dd className="mt-1 text-sm text-gray-900">${parseFloat(item.cost).toFixed(2)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Price</dt>
                    <dd className="mt-1 text-sm text-gray-900">${parseFloat(item.price).toFixed(2)}</dd>
                  </div>
                  {item.preferredVendor && (
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">Preferred Vendor</dt>
                      <dd className="mt-1 text-sm text-gray-900">{item.preferredVendor}</dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Accounting Information */}
              {(item.salesTaxCode || item.account || item.cogsAccount || item.assetAccount || item.taxAgency) && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Accounting Information</h4>
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                    {item.salesTaxCode && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Sales Tax Code</dt>
                        <dd className="mt-1 text-sm text-gray-900">{item.salesTaxCode}</dd>
                      </div>
                    )}
                    {item.account && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Account</dt>
                        <dd className="mt-1 text-sm text-gray-900">{item.account}</dd>
                      </div>
                    )}
                    {item.cogsAccount && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">COGS Account</dt>
                        <dd className="mt-1 text-sm text-gray-900">{item.cogsAccount}</dd>
                      </div>
                    )}
                    {item.assetAccount && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Asset Account</dt>
                        <dd className="mt-1 text-sm text-gray-900">{item.assetAccount}</dd>
                      </div>
                    )}
                    {item.taxAgency && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Tax Agency</dt>
                        <dd className="mt-1 text-sm text-gray-900">{item.taxAgency}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}

              {/* Timestamps */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Record Information</h4>
                <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                  {item.createdAt && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Created</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {new Date(item.createdAt).toLocaleString()}
                      </dd>
                    </div>
                  )}
                  {item.updatedAt && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {new Date(item.updatedAt).toLocaleString()}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
            <button
              onClick={() => onEdit(item)}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit Item
            </button>
            <button
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
